import { useEffect, useCallback, useRef } from "react";

interface UseKeyboardOptions {
  keys: string[];
  onKeyDown: (event: KeyboardEvent) => void;
}

export const useKeyboard = ({ keys, onKeyDown }: UseKeyboardOptions) => {
  // Add a ref to track pressed keys
  const pressedKeys = useRef<Set<string>>(new Set());

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only trigger if key is in our list and hasn't been pressed already
      if (keys.includes(event.key) && !pressedKeys.current.has(event.key)) {
        pressedKeys.current.add(event.key);
        onKeyDown(event);
      }
    },
    [keys, onKeyDown]
  );

  // Add key up handler to remove keys from pressed set
  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (keys.includes(event.key)) {
        pressedKeys.current.delete(event.key);
      }
    },
    [keys]
  );

  useEffect(() => {
    globalThis.addEventListener("keydown", handleKeyDown);
    globalThis.addEventListener("keyup", handleKeyUp);
    return () => {
      globalThis.removeEventListener("keydown", handleKeyDown);
      globalThis.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  return { handleKeyDown };
};
